/**
 * How one renewal reminder reads on the admin panel. Pure and standalone so
 * cron/tests/renewal-delivery-panel.integration.test.ts asserts the label the
 * panel actually renders, rather than a copy of the rule that can drift from it.
 */
export type Reminder = {
  days: number;
  dueDate: string;
  windowClosed: boolean;
  sentDate?: string;
  openedDate?: string;
};

export type ReminderRow = {
  purchaseDate: string;
  pausedUntil?: string;
};

/*
 * A member who paused their payments is skipped by the cron, so a reminder
 * whose due date falls inside the pause reads "Paused" rather than aging into a
 * false "Missed". The comparison is per reminder, not per person: a deferred
 * cohort can have reminders due after the pause ends, and the cron does send
 * those. It also outlives the pause — `pausedUntil` is kept after the pause is
 * over, so the label stays right months later. Reminders already sent keep
 * their "Sent" record; pausing does not rewrite history.
 *
 * The test has no start bound, because nothing records when a pause BEGAN —
 * `paused_until` only records when it ends. So a reminder that genuinely
 * failed shortly before someone paused also reads "Paused". Rare (it needs a
 * real delivery failure followed by a pause) and the label is deliberately
 * worded so it does not claim the reminder fell inside the pause. A
 * `paused_at` column is what would close it.
 *
 * `windowClosed` comes from the server, so "missed" only fires once the cron's
 * 24h window has genuinely elapsed — not at midnight UTC on the due date, which
 * for a non-deferred purchase can be most of a day early.
 *
 * A reminder whose due date predates the purchase never had a window at all —
 * a short trial makes the 14-day reminder unreachable — so it reads "n/a".
 * (dueDate is deferred-anchored while purchaseDate comes from created_at; for a
 * deferred purchase deferred_until is always >= created_at, so the comparison
 * stays conservative and only ever suppresses genuinely impossible reminders.)
 */
export function reminderStatus(reminder: Reminder, row: ReminderRow) {
  if (reminder.sentDate) {
    return {
      kind: 'sent' as const,
      date: reminder.sentDate,
      openedDate: reminder.openedDate,
    };
  }
  if (reminder.dueDate < row.purchaseDate) {
    return { kind: 'notApplicable' as const };
  }
  if (row.pausedUntil && reminder.dueDate <= row.pausedUntil) {
    return { kind: 'paused' as const, date: row.pausedUntil };
  }
  if (reminder.windowClosed) {
    return { kind: 'missed' as const, date: reminder.dueDate };
  }
  return { kind: 'due' as const, date: reminder.dueDate };
}
